import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Loader2, LayoutGrid, PanelLeft } from "lucide-react";

import { TopNav } from "@/components/TopNav";
import { Sidebar } from "@/components/Sidebar";
import { ActivityBar, WorkspaceNavigator, sectionForPath } from "@/components/WorkspaceNav";
import { useNavMode } from "@/contexts/NavModeContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { DefaultFiltersProvider } from "@/contexts/DefaultFiltersContext";
import { ConsultantProvider } from "@/contexts/ConsultantContext";
// ConsultantBubble removed 2026-04-29 — TopNav search field is the
// canonical entry point now; the floating bottom-right pill was visual
// clutter on top of the same affordance.
import { ConsultantDrawer } from "@/components/consultant/ConsultantDrawer";

const ONBOARDING_PATH = "/onboarding/operator-type";

/**
 * Protected-route Layout. Reads auth state from the shared
 * UserProfileProvider context (single source of truth) and redirects to
 * /auth when there is no session. Does NOT mount its own
 * onAuthStateChange / getSession — those run exactly once at the app
 * root via UserProfileProvider, which prevents the auth-lock contention
 * that crashed navigation between the Live Market Dashboard and My Workspace.
 *
 * Also enforces the operator-type onboarding: if the signed-in user has
 * not yet selected an operator type (profile.operator_type is null),
 * redirect them to /onboarding/operator-type. This runs once the profile
 * row has actually loaded, so we don't flash the dashboard first.
 */
export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, profile, loading, profileLoading } = useUserProfile();

  // Nav-mode toggle (opt-in) — "workspace" = Epic-style full-width activity bar
  // + contextual navigator replacing the sidebar; "sidebar" = the classic nav.
  // Persisted to localStorage so it survives reloads while being evaluated.
  const { navMode, setNavMode, engaged } = useNavMode();
  // Honor ?nav=workspace / ?nav=classic as a quick way to switch (and persist).
  useEffect(() => {
    const param = new URLSearchParams(location.search).get("nav");
    if (param === "workspace") setNavMode("workspace");
    else if (param === "classic" || param === "sidebar") setNavMode("sidebar");
  }, [location.search, setNavMode]);
  const toggleNav = () => setNavMode(navMode === "workspace" ? "sidebar" : "workspace");
  const activeSection = sectionForPath(location.pathname);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate("/auth", { replace: true });
      return;
    }
    // Wait for the profile to finish loading before deciding whether to
    // redirect to onboarding. Otherwise the very first render after login
    // would see profile=null and incorrectly redirect.
    if (profileLoading) return;
    if (
      profile &&
      !profile.operator_type &&
      location.pathname !== ONBOARDING_PATH
    ) {
      navigate(ONBOARDING_PATH, { replace: true });
    }
  }, [
    loading,
    session,
    profile,
    profileLoading,
    location.pathname,
    navigate,
  ]);

  // Show the spinner while we're still determining the session, OR while
  // we have a session but haven't yet loaded the profile row — this
  // prevents a flash of the dashboard before the onboarding redirect.
  const waitingForProfile = !!session && profileLoading && !profile;
  if (loading || waitingForProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <DefaultFiltersProvider>
      <ConsultantProvider>
        <div className="min-h-screen flex flex-col w-full print:min-h-0 print:bg-white">
          {/* TopNav uses its own internal `sticky top-0 z-30` — by
              keeping it as a flex item here (no enclosing scroll
              container) and letting the document scroll naturally,
              its sticky positioning latches onto the viewport top
              and stays visible during page scroll. The previous
              setup wrapped the page in a fixed-height flex column
              with `overflow-auto` on <main>, which scrolled content
              INSIDE main — meaning sticky never engaged at the doc
              level and the TopNav scrolled out of view as the user
              scrolled the inner main. */}
          <div className="print:hidden">
            <TopNav />
          </div>
          {navMode === "workspace" && <ActivityBar activeSection={activeSection} />}
          <div className={`flex flex-1 print:block ${navMode === "workspace" ? "flex-col md:flex-row md:items-stretch" : ""}`}>
            <div className={`print:hidden ${navMode === "workspace" ? "md:flex" : ""}`}>
              {navMode === "workspace" ? (
                <WorkspaceNavigator activeSection={activeSection} activePath={location.pathname} />
              ) : (
                <Sidebar />
              )}
            </div>
            <main className="flex-1 min-w-0 print:overflow-visible">
              <div className="max-w-[1600px] mx-auto p-4 md:p-6 print:max-w-none print:p-0">
                <Outlet />
              </div>
            </main>
          </div>

          {/* Quick nav-mode flip — shown once the user has chosen a layout
              (in Settings or via ?nav). The canonical control is in Settings. */}
          {engaged && (
          <button
            onClick={toggleNav}
            title={navMode === "workspace" ? "Switch to classic sidebar nav" : "Try the new workspace nav"}
            className="fixed bottom-3 left-3 z-50 print:hidden flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-md hover:text-foreground"
          >
            {navMode === "workspace" ? (
              <><PanelLeft className="h-3.5 w-3.5" /> Classic nav</>
            ) : (
              <><LayoutGrid className="h-3.5 w-3.5" /> New workspace nav</>
            )}
          </button>
          )}
          {/* Global AI Cattle Consultant — drawer only. The floating
              bottom-right "Ask ClaraLive" bubble was retired 2026-04-29
              in favor of the TopNav search/ask field. */}
          <ConsultantDrawer />
        </div>
      </ConsultantProvider>
    </DefaultFiltersProvider>
  );
}
